import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useMemo } from 'react';
import { selectResourceCollections, selectResourceCollectionsLoading } from './selectors';
import { getResourceCollections } from './actions';
import { ResourceCollection } from './types';
import useCurrentAppInstanceConfigs from 'store/app-instance-configs/hooks/use-current-app-instance-configs.hook';
import { selectBuildingUuid } from 'store/building/selectors';
import { configSelector } from 'store/config/selectors';
import { getUserLocale } from 'utils/getUserLocale';
import { useFlags } from 'launchdarkly-react-client-sdk';

export const useGetResourceCollectionsQuery = () => {
  const { showResourceCollectionsMiniapp: isCollectionsEnabled } = useFlags();
  const dispatch = useDispatch();
  const collections = useSelector(selectResourceCollections);
  const loading = useSelector(selectResourceCollectionsLoading);
  const currentAppInstanceConfigs = useCurrentAppInstanceConfigs();
  const buildingUuid = useSelector(selectBuildingUuid);
  const { locale } = useSelector(configSelector);
  // `lang` is a translating signal, so it follows the USER — getUserLocale, not handleNonISOLocales,
  // which falls back to the BUILDING's locale for any tag outside LocaleEnum (en-AU, en-IN, a bare `hu`)
  // and would hand a Dutch building's language to an Australian user. Every other call in this miniapp
  // already forwards a resolved locale; this one sent a literal 'en'.
  const lang = getUserLocale(locale);
  useEffect(() => {
    if (buildingUuid && isCollectionsEnabled) {
      dispatch(
        getResourceCollections.request({
          buildingUuid,
          lang,
          include: ['translations'],
        }),
      );
    }
  }, [dispatch, buildingUuid, isCollectionsEnabled, lang]);

  const deduplicatedCollections = useMemo(() => {
    const visibleInProduct = collections?.filter(collection => collection.showInProduct !== false);

    if (!visibleInProduct || !currentAppInstanceConfigs.length) {
      return visibleInProduct || [];
    }

    const collectionNameMap = new Map<string, ResourceCollection & { originalUuids: string[] }>();

    visibleInProduct.forEach(collection => {
      const name = collection.name || collection.uuid;
      if (collectionNameMap.has(name)) {
        const existing = collectionNameMap.get(name);
        if (existing && !existing.originalUuids.includes(collection.uuid)) {
          existing.originalUuids.push(collection.uuid);
        }
      } else {
        collectionNameMap.set(name, {
          ...collection,
          originalUuids: [collection.uuid],
        });
      }
    });

    return Array.from(collectionNameMap.values());
  }, [collections, currentAppInstanceConfigs]);

  return {
    data: deduplicatedCollections,
    loading,
    refetch: () => {
      if (buildingUuid) {
        dispatch(
          getResourceCollections.request({
            buildingUuid,
            lang,
            include: ['translations'],
          }),
        );
      }
    },
  };
};
